import { api_fetch, data_or_throw, DeductionDetailJson, DeductionListJson } from "@/api"
import { toast } from "@/components/ui/use-toast"
import { AssignRepaymentDialog } from "@/deduction_detail/AssignRepaymentDialog"
import { DrawerDeduction } from "@/dispute/file_dispute_drawer"
import { BumpMessageDeduction } from "@/bump_message/bump_message_dialog"
import { noProp, unreachable } from "@/utils"
import { useState } from "preact/hooks"
import useLocation from "wouter-preact/use-location"
import { requestBackup, useBackupRequestInFlight } from "."

interface DeductionListNextActionProps {
  deduction: DeductionListJson
}

function MatchOriginalButton({ deduction }: DeductionListNextActionProps) {
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [repaymentDeduction, setRepaymentDeduction] = useState<DeductionDetailJson | null>(null)

  async function handleMatchOriginalClick() {
    setLoading(true)
    const res = await api_fetch<DeductionDetailJson>(`/deduction/${deduction.id}`)
    setLoading(false)
    if (!res.ok) {
      toast({
        title: "Unable to open matcher",
        description: res.error.message,
        variant: "destructive",
      })
      return
    }
    setRepaymentDeduction(data_or_throw(res))
    setDialogOpen(true)
  }

  function handleDialogOpenChange(open: boolean) {
    setDialogOpen(open)
    if (!open) {
      setRepaymentDeduction(null)
    }
  }

  return (
    <>
      <button
        class="px-2 py-1 text-xs bg-amber-500 text-white rounded hover:bg-amber-600 cursor-pointer"
        onClick={noProp(handleMatchOriginalClick)}
        disabled={loading}>
        {loading ? "Loading..." : "Match Original"}
      </button>
      {repaymentDeduction && (
        <AssignRepaymentDialog deduction={repaymentDeduction} open={dialogOpen} onOpenChange={handleDialogOpenChange} />
      )}
    </>
  )
}

function RequestBackupListButton({
  deduction,
  is_rerequest,
}: DeductionListNextActionProps & { is_rerequest: boolean }) {
  let requesting = useBackupRequestInFlight(deduction.id)
  let label = requesting ? "Requesting…" : is_rerequest ? "Re-request Backup" : "Request Backup"

  return (
    <button
      class="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      onClick={noProp(() => requestBackup(deduction, { is_rerequest }))}
      disabled={requesting}
      title={is_rerequest ? "We already requested backup but haven't heard back" : undefined}>
      {label}
    </button>
  )
}

export function DeductionListNextActionCell({ deduction }: DeductionListNextActionProps) {
  let [, setLocation] = useLocation()
  let action = deduction.action
  if (!action) {
    return null
  }
  if (action.type === "request_backup") {
    return <RequestBackupListButton deduction={deduction} is_rerequest={action.is_rerequest} />
  } else if (action.type === "file_dispute") {
    return (
      <button
        class="px-2 py-1 text-xs bg-primary text-white rounded cursor-pointer"
        onClick={noProp(() => DrawerDeduction.set(deduction))}>
        File Dispute
      </button>
    )
  } else if (action.type === "send_followup") {
    return (
      <button
        class="px-2 py-1 text-xs bg-primary text-white rounded cursor-pointer"
        onClick={noProp(() => setLocation(`/deduction/${deduction.id}`))}>
        Send Followup
      </button>
    )
  } else if (action.type === "bump_message") {
    return (
      <button
        class="px-2 py-1 text-xs bg-primary text-white rounded cursor-pointer"
        onClick={noProp(() => BumpMessageDeduction.set(deduction))}>
        Bump Message
      </button>
    )
  } else if (action.type === "waiting_for_response") {
    return <div class="px-2 py-1 text-xs bg-gray-400 text-white rounded cursor-default">Awaiting Response</div>
  } else if (action.type === "waiting_for_document") {
    return <div class="px-2 py-1 text-xs bg-gray-400 text-white rounded cursor-default">Awaiting Document</div>
  } else if (action.type === "assign_task") {
    return <div class="px-2 py-1 text-xs bg-primary text-white rounded cursor-pointer">Assign Task</div>
  } else if (action.type === "review_backup") {
    return <div class="px-2 py-1 text-xs bg-primary text-white rounded cursor-pointer">Review Backup</div>
  } else if (action.type === "match_original") {
    return <MatchOriginalButton deduction={deduction} />
  } else if (action.type === "ask_the_wizard" || action.type === "validate_deduction") {
    return null
  } else {
    unreachable(action)
    return null
  }
}
